import { apiCall } from '../api/ServerApi';
import { API_URL_PATHS } from '../consts/ApiUrlPaths';
import { IPaginationResponse } from '../interfaces/PaginationResponse';
import { ITenant, ProductType } from '../interfaces/Tenant';
import { ITenantDescriptor } from '../interfaces/TenantDescriptor';
import { ITenantMemberEntry, ITenantMemberUser } from '../interfaces/TenantMember';

const { TENANTS_URL_PATH, DESCRIPTORS_URL_PATH, USER_TO_TENANTS_URL_PATH } = API_URL_PATHS;

export enum TenantPlan {
	FREE_TRIAL = 'TRIAL',
	COMMUNITY = 'COMMUNITY'
}
const NOT_A_COMPANY_EMAIL_ERROR_MATCH = 'company email';
const EXPIRED_LICESE_ERROR_MATCH = 'expired';

export async function getUserTenant(): Promise<ITenant> {
	const tenants: ITenant[] = await apiCall('GET', TENANTS_URL_PATH);
	if (!tenants || tenants.length === 0) {
		return null;
	}
	return tenants[0];
}

export const TenantDescriptorValue = {
	TRIAL: 'TRIAL',
	COMMUNITY: 'COMMUNITY'
};

export const getTenantDescriptor = async (): Promise<ITenantDescriptor> => {
	return apiCall('GET', `${DESCRIPTORS_URL_PATH}/tenant`);
};

export const activateProduct = async (tenantId: string): Promise<void> => {
	const body = {
		type: ProductType.SCANNER,
		tenantId
	};
	return apiCall('POST', `${TENANTS_URL_PATH}/${tenantId}/products`, {
		data: body
	});
};

export const hasAccessToProduct = async (userTenant?: ITenant): Promise<boolean> => {
	let tenant: ITenant = null;
	if (userTenant) {
		tenant = { ...userTenant };
	} else {
		tenant = await getUserTenant();
	}
	if (!tenant) {
		return false;
	}
	const productPermissions = tenant.products?.find((p) => p.type === ProductType.SCANNER);
	return !!productPermissions;
};

type ProductLicenseDetails = {
	date: Date;
	isExpired: boolean;
	isLicensedForever: boolean;
};

const getAllProductsLicenseDetails = async (): Promise<Map<ProductType, ProductLicenseDetails>> => {
	const tenant = await getUserTenant();
	if (!tenant) {
		return null;
	}
	const result: Map<ProductType, ProductLicenseDetails> = new Map();
	// eslint-disable-next-line no-unused-expressions
	tenant.products?.forEach((productDetails) => {
		const expirationDate = new Date(productDetails.expirationTime);
		const details: ProductLicenseDetails = {
			date: expirationDate,
			isExpired: expirationDate < new Date(),
			isLicensedForever: tenant.accountPlan !== 'TRIAL'
		};
		result.set(productDetails.type, details);
	});
	return result;
};

export const getProductLicenseDetails = async (
	type: ProductType
): Promise<ProductLicenseDetails> => {
	const allProductsDetails = await getAllProductsLicenseDetails();
	return allProductsDetails.get(type);
};

export async function getTenantUsers(
	searchText?: string,
	pageSize = 3,
	active?: boolean
): Promise<ITenantMemberUser[]> {
	let url = `${USER_TO_TENANTS_URL_PATH}?pageSize=${pageSize}`;
	if (typeof active === 'boolean') {
		url += `&active=${active}`;
	}
	if (searchText) {
		url += `&freeSearch=${encodeURIComponent(searchText)}`;
	}
	const response: IPaginationResponse & { list: ITenantMemberEntry[] } = await apiCall('GET', url);
	return response.list.map((entry) => ({ ...entry.user, active: entry.active }));
}

export async function getActiveTenantUsers(
	searchText?: string,
	pageSize = 3
): Promise<ITenantMemberUser[]> {
	return getTenantUsers(searchText, pageSize, true);
}

export enum ACTIVATION_ERROR {
	GENERAL,
	NO_TENANT,
	NOT_A_COMPANY_EMAIL,
	LICENSE_EXPIRED
}
export const getActivationErrorType = (errorMsg: string): ACTIVATION_ERROR => {
	if (!errorMsg) {
		return ACTIVATION_ERROR.GENERAL;
	}
	if (errorMsg.includes(NOT_A_COMPANY_EMAIL_ERROR_MATCH)) {
		return ACTIVATION_ERROR.NOT_A_COMPANY_EMAIL;
	}
	if (errorMsg.includes(EXPIRED_LICESE_ERROR_MATCH)) {
		return ACTIVATION_ERROR.LICENSE_EXPIRED;
	}
	return ACTIVATION_ERROR.GENERAL;
};
